import React, { useEffect } from 'react';
import { FlowObserverPanelEngine, FlowFooterEngine, useFlowObserverPanelStore } from '@flow-studio/flow-observer-core';
import { ExecutionStatusTabContent, ExecutionLogsTabContent, DashboardTabContent, LogDetailTabContent, ExecutionNodeListTabContent, NodeInspectorTabContent, NodesHierarchyTabContent, ErrorAnalysisDashboard, ConsoleLogTabContent } from '@flow-studio/flow-observer-panel';
import { ZapIcon, ClipboardIcon, InfoIcon, SearchIcon, PipelinesIcon } from '@bizfirst/common-react';
import { HilObservePanel } from './HIL/HilObservePanel.js';

/**
 * Runs once at app startup to register all FlowObserver tabs and footer badges.
 * Cleanup on unmount allows HMR and React StrictMode to re-register cleanly.
 * Returns null — no visual output.
 */
export function FlowObserverInit(): null {
  useEffect(() => {
    FlowObserverPanelEngine.configure({ dataBufferMaxSize: 1000 });

    FlowObserverPanelEngine.registerTab(
      {
        id: 'dashboard',
        labelKey: 'flowObserver.tabs.dashboard.label',
        fallbackLabel: 'Dashboard',
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
          </svg>
        ),
        engineState: 'available',
        showInModes: ['execution', 'evaluation'],
        onActivate: async () => { /* data arrives via pushData from useFlowObserverSignalR */ },
        onClose: () => {
          FlowObserverPanelEngine.clearData('dashboard');
        },
      },
      DashboardTabContent
    );

    FlowObserverPanelEngine.registerTab(
      {
        id: 'execution-status',
        labelKey: 'flowObserver.tabs.executionStatus.label',
        fallbackLabel: 'Execution Status',
        icon: <ZapIcon size={14} />,
        engineState: 'available',
        showInModes: ['execution', 'evaluation'],
        onActivate: async () => { /* data arrives via pushData from useFlowObserverSignalR */ },
        onClose: () => {
          FlowObserverPanelEngine.clearData('execution-status');
        },
      },
      ExecutionStatusTabContent
    );

    FlowObserverPanelEngine.registerTab(
      {
        id: 'execution-logs',
        labelKey: 'flowObserver.tabs.executionLogs.label',
        fallbackLabel: 'Execution Logs',
        icon: <ClipboardIcon size={14} />,
        engineState: 'available',
        showInModes: ['execution', 'evaluation'],
        onActivate: async () => { /* data arrives via pushData from useFlowObserverSignalR */ },
        onClose: () => {
          FlowObserverPanelEngine.clearData('execution-logs');
          FlowObserverPanelEngine.setTabBadge('execution-logs', undefined);
        },
      },
      ExecutionLogsTabContent
    );

    FlowObserverPanelEngine.registerTab(
      {
        id: 'nodes',
        labelKey: 'flowObserver.tabs.nodes.label',
        fallbackLabel: 'Nodes',
        icon: <PipelinesIcon size={14} />,
        engineState: 'available',
        showInModes: ['execution', 'evaluation'],
        onActivate: async () => {},
        onClose: () => {},
      },
      ExecutionNodeListTabContent
    );

    FlowObserverPanelEngine.registerTab(
      {
        id: 'inspector',
        labelKey: 'flowObserver.tabs.inspector.label',
        fallbackLabel: 'Inspector',
        icon: <SearchIcon size={14} />,
        engineState: 'hidden',
        showInModes: ['execution', 'evaluation'],
        onActivate: async () => {},
        onClose: () => {},
      },
      NodeInspectorTabContent
    );

    FlowObserverPanelEngine.registerTab(
      {
        id: 'log-detail',
        labelKey: 'flowObserver.tabs.logDetail.label',
        fallbackLabel: 'Log Detail',
        icon: <InfoIcon size={14} />,
        engineState: 'hidden',
        showInModes: ['execution', 'evaluation'],
        onActivate: async () => { /* data pushed from ExecutionLogsTabContent on entry click */ },
        onClose: () => {
          FlowObserverPanelEngine.clearData('log-detail');
        },
      },
      LogDetailTabContent
    );

    // Tier 2: Advanced Analytics Tabs
    FlowObserverPanelEngine.registerTab(
      {
        id: 'nodes-hierarchy',
        labelKey: 'flowObserver.tabs.nodesHierarchy.label',
        fallbackLabel: 'Nodes Hierarchy',
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="8" y1="6" x2="21" y2="6" />
            <line x1="8" y1="12" x2="21" y2="12" />
            <line x1="8" y1="18" x2="21" y2="18" />
            <line x1="3" y1="6" x2="3" y2="6" />
            <line x1="3" y1="12" x2="3" y2="12" />
            <line x1="3" y1="18" x2="3" y2="18" />
          </svg>
        ),
        engineState: 'available',
        showInModes: ['execution', 'evaluation'],
        onActivate: async () => {},
        onClose: () => {},
      },
      NodesHierarchyTabContent
    );

    FlowObserverPanelEngine.registerTab(
      {
        id: 'hil-actions',
        labelKey: 'flowObserver.tabs.hilActions.label',
        fallbackLabel: 'HIL Actions',
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
        ),
        engineState: 'available',
        showInModes: ['execution', 'evaluation'],
        onActivate: async () => {},
        onClose: () => {},
      },
      HilObservePanel as React.ComponentType<any>
    );

    FlowObserverPanelEngine.registerTab(
      {
        id: 'error-analysis',
        labelKey: 'flowObserver.tabs.errorAnalysis.label',
        fallbackLabel: 'Error Analysis',
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        ),
        engineState: 'available',
        showInModes: ['execution', 'evaluation'],
        onActivate: async () => {},
        onClose: () => {},
      },
      ErrorAnalysisDashboard
    );

    FlowObserverPanelEngine.registerTab(
      {
        id: 'console-log',
        labelKey: 'flowObserver.tabs.consoleLog.label',
        fallbackLabel: 'Console Log',
        icon: (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="4 17 10 11 4 5" />
            <line x1="12" y1="19" x2="20" y2="19" />
          </svg>
        ),
        engineState: 'available',
        showInModes: ['execution', 'evaluation'],
        onActivate: async () => {},
        onClose: () => {
          FlowObserverPanelEngine.clearData('console-log');
          FlowObserverPanelEngine.setTabBadge('console-log', undefined);
        },
      },
      ConsoleLogTabContent
    );

    FlowFooterEngine.registerBadge({
      id: 'connection-status',
      type: 'status',
      anchor: 'left',
      order: 1,
      labelKey: 'flowFooter.connection',
      fallbackLabel: 'Connected',
      state: 'active',
      visibleIn: ['all'],
      visible: true,
      interaction: { type: 'none' },
    });

    FlowFooterEngine.registerBadge({
      id: 'designer-status',
      type: 'state',
      anchor: 'left',
      order: 2,
      labelKey: 'flowFooter.designer.ready',
      fallbackLabel: 'Designer: Ready',
      state: 'idle',
      visibleIn: ['designer'],
      visible: true,
      interaction: { type: 'none' },
    });

    FlowFooterEngine.registerBadge({
      id: 'active-workflow',
      type: 'text',
      anchor: 'left',
      order: 3,
      labelKey: 'flowFooter.activeWorkflow',
      fallbackLabel: '',
      state: 'idle',
      visibleIn: ['designer'],
      visible: false, // shown/updated by WorkflowEditor when a thread is loaded
      interaction: { type: 'none' },
    });

    FlowFooterEngine.registerBadge({
      id: 'execution-status',
      type: 'state',
      anchor: 'left',
      order: 4,
      icon: <ZapIcon size={12} />,
      labelKey: 'flowFooter.execution.idle',
      fallbackLabel: 'Idle',
      state: 'idle',
      visibleIn: ['designer'],
      visible: true,
      interaction: { type: 'activateFlowObserverTab', tabId: 'execution-status' },
    });

    FlowFooterEngine.registerBadge({
      id: 'notifications',
      type: 'count',
      anchor: 'right',
      order: 1,
      labelKey: 'flowFooter.notifications',
      fallbackLabel: 'Notifications',
      state: 'idle',
      badgeAriaKey: 'flowFooter.notifications.aria',
      visibleIn: ['all'],
      visible: true,
      interaction: { type: 'custom', actionKey: 'openNotifications' },
    });

    FlowFooterEngine.registerBadge({
      id: 'hil-pending',
      type: 'count',
      anchor: 'right',
      order: 2,
      labelKey: 'flowFooter.hilPending',
      fallbackLabel: 'HIL',
      state: 'idle',
      badgeAriaKey: 'flowFooter.hilPending.aria',
      visibleIn: ['all'],
      visible: false,
      interaction: { type: 'activateFlowObserverTab', tabId: 'hil-actions' },
    });

    FlowFooterEngine.registerBadge({
      id: 'app-version',
      type: 'text',
      anchor: 'right',
      order: 10,
      labelKey: 'flowFooter.appVersion',
      fallbackLabel: 'v1.0.0',
      state: 'idle',
      visibleIn: ['all'],
      visible: true,
      interaction: { type: 'none' },
    });

    // Restore tab visibility and order from localStorage.
    // forceOpenTab is used instead of openTab/activateTab because at init time the designer
    // mode is 'design' and all tabs have engineState='hidden' (they show in execution/evaluation
    // only). activateTab silently returns early when engineState='hidden'. forceOpenTab bypasses
    // that check and sets userState='open' directly — the tabs will render once the mode
    // subscription flips engineState to 'available' when the user enters execution mode.
    const initializeTabs = () => {
      try {
        const store = useFlowObserverPanelStore.getState();

        const defaultTabs = [
          'dashboard',
          'execution-status',
          'execution-logs',
          'error-analysis',
          'nodes-hierarchy',
          'nodes',
          'hil-actions',
        ];

        const openTabsJson = localStorage.getItem('flowObserver.openTabs');
        const hasSavedState = openTabsJson !== null;

        // Always discard any persisted tab order so dashboard stays first
        localStorage.removeItem('flowObserver.tabOrder');

        if (hasSavedState) {
          const openTabIds: string[] = JSON.parse(openTabsJson!);

          // Ensure newly added default tabs are always open even when saved state exists
          defaultTabs.forEach(id => {
            if (!openTabIds.includes(id)) openTabIds.push(id);
          });

          // Sync tab userState to match the saved list
          Object.keys(store.tabRuntimeStates).forEach(id => {
            if (!openTabIds.includes(id)) {
              store.closeTab(id);
            }
          });
          openTabIds.forEach(id => {
            if (store.tabRuntimeStates[id]) {
              store.forceOpenTab(id);
            }
          });
        } else {
          // First visit — open defaults
          defaultTabs.forEach(id => {
            if (store.tabRuntimeStates[id]) store.forceOpenTab(id);
          });
        }

        // Always enforce defaultTabs order so dashboard is position 0
        defaultTabs.forEach((id, index) => {
          if (store.tabRuntimeStates[id]) store.reorderTab(id, index);
        });
      } catch (e) {
        console.warn('Failed to restore tab visibility from localStorage', e);
      }
    };

    // Set initial tab: restore from localStorage or default to 'dashboard'
    const initializeActiveTab = () => {
      const lastTab = 'dashboard';
      try {
        FlowObserverPanelEngine.activateTab(lastTab);
      } catch (e) {
        console.warn(`Failed to activate tab ${lastTab}, trying dashboard`, e);
        try {
          FlowObserverPanelEngine.activateTab('dashboard');
        } catch (fallbackError) {
          console.warn('Failed to activate dashboard tab', fallbackError);
        }
      }
    };

    setTimeout(() => {
      initializeTabs();
      initializeActiveTab();
    }, 50);

    return () => {
      FlowObserverPanelEngine.unregisterTab('execution-status');
      FlowObserverPanelEngine.unregisterTab('execution-logs');
      FlowObserverPanelEngine.unregisterTab('dashboard');
      FlowObserverPanelEngine.unregisterTab('nodes');
      FlowObserverPanelEngine.unregisterTab('inspector');
      FlowObserverPanelEngine.unregisterTab('log-detail');
      FlowObserverPanelEngine.unregisterTab('console-log');
      FlowFooterEngine.unregisterBadge('connection-status');
      FlowFooterEngine.unregisterBadge('designer-status');
      FlowFooterEngine.unregisterBadge('active-workflow');
      FlowFooterEngine.unregisterBadge('execution-status');
      FlowFooterEngine.unregisterBadge('notifications');
      FlowFooterEngine.unregisterBadge('hil-pending');
      FlowFooterEngine.unregisterBadge('app-version');
    };
  }, []);

  return null;
}
